import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Banknote, ArrowRightLeft, HandCoins, Wallet, Scale, PiggyBank } from "lucide-react";
import { PERIODS, LABELS } from "@/components/RevenuePerPtPanel";

const PERIOD_SUFFIX = { today: "Hari Ini", week: "Minggu Ini", month: "Bulan Ini", custom: "Periode" };

function Tile({ icon: Icon, label, value, sub, rows, accent, testId, onClick }) {
  return (
    <div onClick={onClick} className={`rounded-xl border bg-white p-3.5 ${onClick ? "cursor-pointer hover:shadow-md hover:border-brand/40 transition-[box-shadow,border-color]" : ""}`} data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-3.5 w-3.5" /></span>
        <p className="text-xs text-muted-foreground leading-tight">{label}</p>
      </div>
      <p className="text-lg font-bold font-mono mt-2 truncate" data-testid={`${testId}-value`}>{formatRupiah(value)}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
      {rows?.length > 0 && (
        <div className="mt-1.5 pt-1.5 border-t space-y-0.5">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-2 text-[11px] text-muted-foreground">
              <span className="truncate font-mono">{r.label}</span><span className="font-mono shrink-0">{formatRupiah(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PemohonRevenuePanel() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");
  const [custom, setCustom] = useState(PERIODS.today());
  const [d, setD] = useState(null);
  const [bal, setBal] = useState(null);
  useEffect(() => { api.get("/cash-deposits/balance").then((r) => setBal(r.data)).catch(() => {}); }, []);
  const [start, end] = period === "custom" ? custom : PERIODS[period]();
  const sfx = PERIOD_SUFFIX[period];

  useEffect(() => {
    api.get("/my-revenue", { params: { start_date: start || undefined, end_date: end || undefined } })
      .then((r) => setD(r.data)).catch(() => {});
  }, [start, end]);

  if (!d) return null;
  return (
    <Card className="p-4" data-testid="pemohon-revenue-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Ringkasan Pendapatan</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5 bg-secondary">
            {Object.entries(LABELS).map(([k, l]) => (
              <button key={k} onClick={() => setPeriod(k)} data-testid={`my-revenue-period-${k}`}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${period === k ? "bg-white shadow-sm font-semibold text-brand" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-1">
              <Input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="h-8 w-36 text-xs" data-testid="my-revenue-start" />
              <span className="text-xs">–</span>
              <Input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="h-8 w-36 text-xs" data-testid="my-revenue-end" />
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
        <Tile icon={Banknote} label={`Transaksi Cash ${sfx}`} value={d.cash} sub={`${d.cash_count} nota`} accent="bg-amber-100 text-amber-700" testId="my-revenue-cash" />
        <Tile icon={ArrowRightLeft} label={`Transaksi Transfer ${sfx}`} value={d.transfer} sub={`${d.transfer_count} nota`} rows={d.transfer_by_account} accent="bg-sky-100 text-sky-700" testId="my-revenue-transfer" />
        <Tile icon={HandCoins} label={`Pembayaran Piutang ${sfx}`} value={d.piutang} sub={`${d.piutang_count} pembayaran`} rows={d.piutang_by} accent="bg-violet-100 text-violet-700" testId="my-revenue-piutang" />
        <Tile icon={Wallet} label={`Total Pendapatan ${sfx}`} value={d.total} sub="Cash + Transfer + Piutang" accent="bg-emerald-100 text-emerald-700" testId="my-revenue-total" />
        <Tile icon={Scale} label="Saldo Piutang Berjalan" value={d.receivable_balance} sub="Total piutang aktif saat ini" accent="bg-rose-100 text-rose-700" testId="my-revenue-receivable" onClick={() => navigate("/receivables")} />
        {bal && <Tile icon={PiggyBank} label="Tunai Pemohon (Dipegang)" value={bal.cash_pemohon ?? bal.balance} sub={`Belum disetor ${formatRupiah(bal.balance)} · menunggu konfirmasi ${formatRupiah(bal.pending || 0)} · Tunai Pusat ${formatRupiah(bal.cash_pusat || 0)}`} accent="bg-emerald-100 text-brand" testId="my-revenue-cash-balance" onClick={() => navigate("/cash-deposits")} />}
      </div>
    </Card>
  );
}
